import axios from "axios";
import { getAuthHeaders } from "../Auth/authHelpers";

let fetchPatched = false;
let axiosInterceptorId = null;

const getApiBase = () => process.env.REACT_APP_API_URL || "";

const getTrainerId = () => sessionStorage.getItem("trainerID") || "";

const shouldAttach = (url, apiBase) =>
	apiBase && typeof url === "string" && url.startsWith(apiBase);

// Global interceptor that attaches the Azure AD bearer token to every request
// hitting the ETP backend. Any fetch/axios call in the app that doesn't
// explicitly set an Authorization header will get one here.
export const setupApiAuth = (trainerID) => {
	const apiBase = getApiBase();
	if (!apiBase) return;

	if (trainerID) {
		sessionStorage.setItem("trainerID", trainerID);
	}

	if (!fetchPatched && typeof window !== "undefined" && window.fetch) {
		const originalFetch = window.fetch.bind(window);

		window.fetch = async (input, init = {}) => {
			const url = typeof input === "string" ? input : input?.url;

			if (shouldAttach(url, apiBase)) {
				// Token comes from MSAL directly; trainerId is only used for the
				// legacy x-trainer-id telemetry header. Attach auth even if the
				// sessionStorage trainerId isn't populated yet (e.g. components
				// that mount before App.js finishes initAuth).
				const trainerId = getTrainerId();
				const headers = new Headers(init.headers || {});
				if (!headers.has("Authorization")) {
					try {
						const auth = await getAuthHeaders(trainerId);
						Object.entries(auth).forEach(([k, v]) => {
							if (!headers.has(k)) headers.set(k, v);
						});
					} catch (e) {
						// If token acquisition fails, let the original request go
						// out unauthenticated so the backend returns 401 and the
						// UI can react (e.g. redirect to login) instead of hanging.
						console.error("Auth header injection failed:", e);
					}
				}
				return originalFetch(input, { ...init, headers });
			}

			return originalFetch(input, init);
		};

		fetchPatched = true;
	}

	if (axiosInterceptorId !== null) {
		axios.interceptors.request.eject(axiosInterceptorId);
	}

	axiosInterceptorId = axios.interceptors.request.use(async (config) => {
		const apiBaseNow = getApiBase();
		if (!apiBaseNow) return config;
		const trainerId = getTrainerId();

		const url = config.url || "";
		const fullUrl = url.startsWith("http")
			? url
			: `${apiBaseNow}${url.startsWith("/") ? "" : "/"}${url}`;

		if (fullUrl.startsWith(apiBaseNow)) {
			try {
				const auth = await getAuthHeaders(trainerId);
				config.headers = {
					...(config.headers || {}),
					...auth,
				};
			} catch (e) {
				console.error("Auth header injection failed (axios):", e);
			}
		}

		return config;
	});
};
